import { getPilotTheme } from '../../config/partnerThemes';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../services/api_config';

const SAFEOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Each step starts at the top of the page. Short steps hid this; taller ones stayed
  // scrolled down where Continue was pressed.
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (e) { window.scrollTo(0, 0); }
  }, [step]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState('');

  const [profile, setProfile] = useState({
    preferred_name: '',
    wellness_goal: '',
    stress_level: '',
    support_style: '',
    life_stage: '',
    military_status: 'none',
    branch: '',
    years_service: null,
    discharge_status: '',
    primary_challenges: [],
    faith_preference: '',
    scripture_optin: false,
    current_situation: '',
    working_toward: [],
    family: { relationship: '', partner_role: '', partner_name: '', children: [], grandchildren: [], other: '' },
    occupation: '',
    coach_tone: 'warm'
  });

  // Step count: military adds 1 extra step for service details
  const totalSteps = profile.military_status !== 'none' ? 9 : 8;

  // ── Options ──────────────────────────────────────────────────────────────

  const wellnessGoalOptions = [
    { value: 'stress_anxiety', label: 'Stress & Anxiety', icon: '😟' },
    { value: 'grief_loss', label: 'Grief & Loss', icon: '💔' },
    { value: 'relationships', label: 'Relationships', icon: '🤝' },
    { value: 'burnout', label: 'Burnout & Exhaustion', icon: '🔥' },
    { value: 'trauma', label: 'Trauma & Healing', icon: '🌱' },
    { value: 'general_wellness', label: 'General Wellness', icon: '💚' }
  ];

  const stressLevelOptions = [
    { value: 'low', label: 'Low — I\'m doing well overall', icon: '😊', color: '#059669' },
    { value: 'moderate', label: 'Moderate — Some stress but managing', icon: '😐', color: '#f59e0b' },
    { value: 'high', label: 'High — Struggling most days', icon: '😟', color: '#ef4444' },
    { value: 'crisis', label: 'Crisis — I really need support now', icon: '🆘', color: '#dc2626' }
  ];

  const supportStyleOptions = [
    { value: 'listening_ear', label: 'A Listening Ear', desc: 'I need someone to hear me without judgment', icon: '👂' },
    { value: 'practical_tools', label: 'Practical Tools', desc: 'Give me strategies and actionable steps', icon: '🛠️' },
    { value: 'faith_based', label: 'Faith-Based Guidance', desc: 'Ground my support in spiritual wisdom', icon: '🙏' },
    { value: 'mindfulness_based', label: 'Mindfulness & Reflection', desc: 'Help me slow down and process', icon: '🧘' }
  ];

  const lifeStageOptions = [
    { value: 'young_adult', label: 'Young Adult', desc: 'Finding my way (18–29)', icon: '🌟' },
    { value: 'career_family', label: 'Career & Family Building', desc: 'Juggling work and relationships (30–44)', icon: '🏗️' },
    { value: 'midlife', label: 'Midlife', desc: 'Reflecting and recalibrating (45–60)', icon: '🔄' },
    { value: 'senior', label: 'Senior', desc: 'Wisdom and transitions (60+)', icon: '🌅' },
    { value: 'caregiver', label: 'Caregiver', desc: 'Supporting others while caring for myself', icon: '🤲' }
  ];

  const militaryStatusOptions = [
    { value: 'none', label: 'No military connection', icon: '👤' },
    { value: 'active', label: 'Active Duty', icon: '🎖️' },
    { value: 'veteran', label: 'Veteran', icon: '🏅' },
    { value: 'military_family', label: 'Military Family Member', icon: '👨‍👩‍👧‍👦' }
  ];

  const branchOptions = [
    { value: 'army', label: 'Army' },
    { value: 'navy', label: 'Navy' },
    { value: 'air_force', label: 'Air Force' },
    { value: 'marines', label: 'Marines' },
    { value: 'coast_guard', label: 'Coast Guard' },
    { value: 'space_force', label: 'Space Force' },
    { value: 'national_guard', label: 'National Guard/Reserves' }
  ];

  const dischargeOptions = [
    { value: 'active', label: 'Currently Active' },
    { value: 'reserves', label: 'Reserves' },
    { value: 'retired', label: 'Retired' },
    { value: 'medical', label: 'Medically Discharged' },
    { value: 'honorable', label: 'Honorably Discharged' }
  ];

  // Where the person is now, their work, family and what they are working toward: asked of everyone.
  // HERO's own wording (veteran examples, the full-framework secular card). Stand-alone SAFE uses SAFE's.
  const pilotSetup = !!getPilotTheme() && !getPilotTheme().standard;
  const situationOptions = [
    { value: 'working', label: 'Working' },
    { value: 'school', label: 'In school or training' },
    { value: 'job_seeking', label: 'Looking for work' },
    { value: 'own_business', label: 'Running my own business' },
    { value: 'caregiving', label: 'Caring for family' },
    { value: 'retired', label: 'Retired' },
    { value: 'not_working', label: 'Not working right now' }
  ];
  const towardOptions = [
    { value: 'work', label: 'Steady work or a new career' },
    { value: 'school', label: 'Finishing school or training' },
    { value: 'build', label: 'Building something of my own' },
    { value: 'family', label: 'Being there for my family' },
    { value: 'health', label: 'Getting healthier' },
    { value: 'people', label: 'Reconnecting with people' },
    { value: 'purpose', label: 'A sense of purpose' },
    { value: 'getting_through', label: 'Getting through this stretch' },
    { value: 'unsure', label: 'Not sure yet' }
  ];
  const toggleToward = (value) => {
    setProfile(prev => {
      const cur = prev.working_toward || [];
      if (cur.includes(value)) return { ...prev, working_toward: cur.filter(v => v !== value) };
      if (cur.length >= 2) return prev;
      return { ...prev, working_toward: [...cur, value] };
    });
  };

  const challengeOptions = [
    'Anxiety', 'Depression', 'Stress', 'Sleep Issues', 'Anger',
    'Grief/Loss', 'Relationships', 'Life Transitions', 'Trauma',
    'Loneliness', 'Self-Esteem', 'Work/Life Balance', 'Motivation'
  ];

  // ── Load existing profile ─────────────────────────────────────────────────

  useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const response = await API.get('/api/v1/safe/profile');
      if (response.data) {
        setProfile({
          preferred_name: response.data.preferred_name || '',
          wellness_goal: response.data.wellness_goal || '',
          stress_level: response.data.stress_level || '',
          support_style: response.data.support_style || '',
          life_stage: response.data.life_stage || '',
          military_status: response.data.military_status || 'none',
          branch: response.data.branch || '',
          years_service: response.data.years_service || null,
          discharge_status: response.data.discharge_status || '',
          primary_challenges: response.data.primary_challenges || [],
          faith_preference: response.data.faith_preference || '',
          scripture_optin: !!response.data.scripture_optin,
          current_situation: response.data.current_situation || '',
          working_toward: response.data.working_toward || [],
          family: { ...{ relationship: '', partner_role: '', partner_name: '', children: [], grandchildren: [], other: '' }, ...(response.data.family || {}) },
          occupation: response.data.occupation || '',
          coach_tone: response.data.coach_tone || 'warm'
        });
        setIsEditMode(true);
      }
    } catch (err) {
      console.log('No existing profile');
    }
  };

  const toggleChallenge = (challenge) => {
    setProfile(prev => ({
      ...prev,
      primary_challenges: prev.primary_challenges.includes(challenge)
        ? prev.primary_challenges.filter(c => c !== challenge)
        : [...prev.primary_challenges, challenge]
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    try {
      await API.post('/api/v1/safe/profile', profile);
      navigate('/safe');
    } catch (err) {
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step navigation helpers ───────────────────────────────────────────────

  // Map logical step numbers accounting for the conditional military detail step
  // Steps: 1=name, 2=wellness_goal, 3=stress_level, 4=support_style, 5=life_stage,
  //        6=military_status, 7=service_details(conditional), 8(or 7)=challenges, 9(or 8)=faith
  const getStepLabel = () => {
    if (step === 1) return 'Your Name';
    if (step === 2) return 'Wellness Focus';
    if (step === 3) return 'Stress Level';
    if (step === 4) return 'Support Style';
    if (step === 5) return 'Life Stage & Family';
    if (step === 6) return 'Background';
    if (step === 7 && profile.military_status !== 'none') return 'Service Details';
    if ((step === 7 && profile.military_status === 'none') || step === 8) return 'Challenges';
    return 'Guidance Style';
  };

  // -- Family (pilot) --
  const setFamily = (patch) => setProfile(prev => ({ ...prev, family: { ...(prev.family || {}), ...patch } }));
  const familyRows = (key) => ((profile.family || {})[key] || []);
  const setFamilyRow = (key, i, patch) => setFamily({ [key]: familyRows(key).map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const addFamilyRow = (key) => setFamily({ [key]: [...familyRows(key), { name: '', age: '' }] });
  const removeFamilyRow = (key, i) => setFamily({ [key]: familyRows(key).filter((_, j) => j !== i) });
  const familyInput = { padding: '12px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', color: 'white', fontSize: '16px', fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0 };
  const familyLabel = { color: 'rgba(255,255,255,0.85)', fontSize: '15px', fontWeight: 600, display: 'block', margin: '22px 0 10px' };
  const relationshipOptions = [
    { value: 'married', label: 'Married' }, { value: 'relationship', label: 'In a relationship' },
    { value: 'single', label: 'Single' }, { value: 'separated', label: 'Separated or divorced' },
    { value: 'widowed', label: 'Widowed' },
  ];

  const canContinue = () => {
    if (step === 1) return profile.preferred_name.trim() !== '';
    if (step === 2) return profile.wellness_goal !== '';
    if (step === 3) return profile.stress_level !== '';
    if (step === 4) return profile.support_style !== '';
    if (step === 5) return profile.life_stage !== '';
    return true;
  };

  const isLastStep = step === totalSteps;
  const canSubmit = profile.faith_preference !== '';

  // ── Styles ────────────────────────────────────────────────────────────────

  const cardStyle = (selected, color = '#4A90E2') => ({
    padding: '16px',
    background: selected ? `rgba(${color === '#4A90E2' ? '74,144,226' : '5,150,105'},0.25)` : 'rgba(255,255,255,0.05)',
    border: selected ? `2px solid ${color}` : '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  });

  const chipStyle = (selected) => ({
    padding: '10px 16px',
    borderRadius: '20px',
    cursor: 'pointer',
    background: selected ? 'rgba(74,144,226,0.3)' : 'rgba(255,255,255,0.1)',
    border: selected ? '2px solid #4A90E2' : '1px solid rgba(255,255,255,0.2)',
    color: 'white',
    fontSize: '14px',
    transition: 'all 0.2s ease'
  });

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      <div style={{ maxWidth: '500px', width: '100%' }}>

        <h1 style={{ color: 'white', textAlign: 'center', marginBottom: '4px' }}>
          {isEditMode ? 'Update Your Profile' : 'Welcome to SAFE'}
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center', marginBottom: '8px' }}>
          Self-Awareness and Feelings Expression
        </p>
        <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontSize: '13px', marginBottom: '28px' }}>
          {getStepLabel()} — Step {step} of {totalSteps}
        </p>

        {/* Progress bar */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginBottom: '32px' }}>
          {[...Array(totalSteps)].map((_, i) => (
            <div key={i} style={{
              flex: 1, height: '4px', borderRadius: '2px',
              background: i + 1 <= step ? '#4A90E2' : 'rgba(255,255,255,0.2)',
              transition: 'background 0.3s ease'
            }} />
          ))}
        </div>

        {error && (
          <div style={{ background: 'rgba(255,0,0,0.2)', padding: '12px', borderRadius: '8px', marginBottom: '16px', color: '#ff6b6b' }}>
            {error}
          </div>
        )}

        {/* ── STEP 1: Name ── */}
        {step === 1 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>What should I call you?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              This is a safe space. Use whatever name feels right.
            </p>
            <input
              type="text"
              value={profile.preferred_name}
              onChange={(e) => setProfile({...profile, preferred_name: e.target.value})}
              placeholder="Your preferred name"
              style={{
                width: '100%', padding: '16px', borderRadius: '12px',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
                color: 'white', fontSize: '16px', outline: 'none', boxSizing: 'border-box'
              }}
            />
          </div>
        )}

        {/* ── STEP 2: Wellness Goal ── */}
        {step === 2 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>What brings you to SAFE today?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              Choose the area you'd most like support with right now.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {wellnessGoalOptions.map(opt => (
                <div key={opt.value} onClick={() => setProfile({...profile, wellness_goal: opt.value})}
                  style={cardStyle(profile.wellness_goal === opt.value)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.icon}</span>
                    <span style={{ color: 'white', fontSize: '15px' }}>{opt.label}</span>
                    {profile.wellness_goal === opt.value && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 3: Stress Level ── */}
        {step === 3 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>How are you feeling right now?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              Be honest — this helps SAFE meet you where you are.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {stressLevelOptions.map(opt => (
                <div key={opt.value} onClick={() => setProfile({...profile, stress_level: opt.value})}
                  style={{
                    padding: '16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s ease',
                    background: profile.stress_level === opt.value ? `${opt.color}33` : 'rgba(255,255,255,0.05)',
                    border: profile.stress_level === opt.value ? `2px solid ${opt.color}` : '1px solid rgba(255,255,255,0.15)'
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.icon}</span>
                    <span style={{ color: 'white', fontSize: '15px' }}>{opt.label}</span>
                    {profile.stress_level === opt.value && <span style={{ marginLeft: 'auto', color: opt.color }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 4: Support Style ── */}
        {step === 4 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>How do you prefer to be supported?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              SAFE will adapt its approach to what works best for you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {supportStyleOptions.map(opt => (
                <div key={opt.value} onClick={() => setProfile({...profile, support_style: opt.value})}
                  style={cardStyle(profile.support_style === opt.value)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.icon}</span>
                    <div>
                      <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{opt.label}</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{opt.desc}</div>
                    </div>
                    {profile.support_style === opt.value && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 5: Life Stage ── */}
        {step === 5 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>What life stage are you in?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              This helps SAFE speak to where you are in your journey.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {lifeStageOptions.map(opt => (
                <div key={opt.value} onClick={() => setProfile({...profile, life_stage: opt.value})}
                  style={cardStyle(profile.life_stage === opt.value)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.icon}</span>
                    <div>
                      <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{opt.label}</div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{opt.desc}</div>
                    </div>
                    {profile.life_stage === opt.value && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
            {(
              <div style={{ marginTop: '36px' }}>
                <h2 style={{ color: 'white', marginBottom: '8px' }}>Your family</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '6px' }}>Optional. It lets SAFE talk with you about the people in your life by name. Share only what you want to.</p>
                <label style={familyLabel}>Relationship</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {relationshipOptions.map(opt => (
                    <span key={opt.value} onClick={() => setFamily({ relationship: (profile.family || {}).relationship === opt.value ? '' : opt.value })}
                      style={chipStyle((profile.family || {}).relationship === opt.value)}>{opt.label}</span>
                  ))}
                </div>
                {['married', 'relationship'].includes((profile.family || {}).relationship) && (
                  <>
                    <label style={familyLabel}>Your spouse or partner</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                      {['husband', 'wife', 'partner'].map(role => (
                        <span key={role} onClick={() => setFamily({ partner_role: (profile.family || {}).partner_role === role ? '' : role })}
                          style={chipStyle((profile.family || {}).partner_role === role)}>{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                      ))}
                    </div>
                    <input type="text" value={(profile.family || {}).partner_name || ''} maxLength={30} placeholder="First name (optional)" aria-label="Spouse or partner's first name"
                      onChange={(e) => setFamily({ partner_name: e.target.value })} style={{ ...familyInput, width: '100%' }} />
                  </>
                )}
                <label style={familyLabel}>Children</label>
                {familyRows('children').map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input type="text" value={r.name || ''} maxLength={30} placeholder="First name (optional)" aria-label={'Child ' + (i + 1) + ' name'}
                      onChange={(e) => setFamilyRow('children', i, { name: e.target.value })} style={{ ...familyInput, flex: 1 }} />
                    <input type="number" inputMode="numeric" min="0" max="120" value={r.age ?? ''} placeholder="Age" aria-label={'Child ' + (i + 1) + ' age'}
                      onChange={(e) => setFamilyRow('children', i, { age: e.target.value })} style={{ ...familyInput, width: '84px', flex: '0 0 84px' }} />
                    <button type="button" onClick={() => removeFamilyRow('children', i)} aria-label={'Remove Child ' + (i + 1)}
                      style={{ width: '44px', flex: '0 0 44px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', color: 'rgba(255,255,255,0.8)', fontSize: '20px', cursor: 'pointer' }}>{'\u00D7'}</button>
                  </div>
                ))}
                <button type="button" onClick={() => addFamilyRow('children')}
                  style={{ padding: '10px 14px', background: 'transparent', border: '1px dashed rgba(255,255,255,0.35)', borderRadius: '10px', color: 'white', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>+ Add a child</button>
                <label style={familyLabel}>Grandchildren</label>
                {familyRows('grandchildren').map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input type="text" value={r.name || ''} maxLength={30} placeholder="First name (optional)" aria-label={'Grandchild ' + (i + 1) + ' name'}
                      onChange={(e) => setFamilyRow('grandchildren', i, { name: e.target.value })} style={{ ...familyInput, flex: 1 }} />
                    <input type="number" inputMode="numeric" min="0" max="120" value={r.age ?? ''} placeholder="Age" aria-label={'Grandchild ' + (i + 1) + ' age'}
                      onChange={(e) => setFamilyRow('grandchildren', i, { age: e.target.value })} style={{ ...familyInput, width: '84px', flex: '0 0 84px' }} />
                    <button type="button" onClick={() => removeFamilyRow('grandchildren', i)} aria-label={'Remove Grandchild ' + (i + 1)}
                      style={{ width: '44px', flex: '0 0 44px', background: 'transparent', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', color: 'rgba(255,255,255,0.8)', fontSize: '20px', cursor: 'pointer' }}>{'\u00D7'}</button>
                  </div>
                ))}
                <button type="button" onClick={() => addFamilyRow('grandchildren')}
                  style={{ padding: '10px 14px', background: 'transparent', border: '1px dashed rgba(255,255,255,0.35)', borderRadius: '10px', color: 'white', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' }}>+ Add a grandchild</button>
                <label style={familyLabel}>Anyone else at home or close to you?</label>
                <input type="text" value={(profile.family || {}).other || ''} maxLength={120} placeholder="For example: my mom lives with us"
                  aria-label="Anyone else at home or close to you"
                  onChange={(e) => setFamily({ other: e.target.value })} style={{ ...familyInput, width: '100%' }} />
              </div>
            )}
          </div>
        )}

        {/* ── STEP 6: Military Status ── */}
        {step === 6 && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>Do you have a military connection?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              This unlocks specialized support for military and veteran experiences.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {militaryStatusOptions.map(opt => (
                <div key={opt.value}
                  onClick={() => setProfile({...profile, military_status: opt.value, branch: '', years_service: null, discharge_status: ''})}
                  style={cardStyle(profile.military_status === opt.value)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{opt.icon}</span>
                    <span style={{ color: 'white', fontSize: '15px' }}>{opt.label}</span>
                    {profile.military_status === opt.value && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 7: Service Details (military only) ── */}
        {step === 7 && profile.military_status !== 'none' && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '16px' }}>Service Details</h2>
            <label style={{ color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '8px' }}>Branch</label>
            <select value={profile.branch} onChange={(e) => setProfile({...profile, branch: e.target.value})}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', background: '#2a3a4a', border: '1px solid rgba(255,255,255,0.2)', color: 'white', marginBottom: '16px' }}>
              <option value="">Select branch...</option>
              {branchOptions.map(b => <option key={b.value} value={b.value} style={{background: '#2a3a4a'}}>{b.label}</option>)}
            </select>
            <label style={{ color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '8px' }}>Years of Service</label>
            <input type="number" value={profile.years_service || ''} onChange={(e) => setProfile({...profile, years_service: parseInt(e.target.value) || null})}
              placeholder="Years" style={{ width: '100%', padding: '12px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: 'white', marginBottom: '16px', boxSizing: 'border-box' }} />
            <label style={{ color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '8px' }}>Current Status</label>
            <select value={profile.discharge_status} onChange={(e) => setProfile({...profile, discharge_status: e.target.value})}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', background: '#2a3a4a', border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}>
              <option value="">Select status...</option>
              {dischargeOptions.map(d => <option key={d.value} value={d.value} style={{background: '#2a3a4a'}}>{d.label}</option>)}
            </select>
          </div>
        )}

        {/* ── STEP 7 (no military) or STEP 8 (military): Challenges ── */}
        {((step === 7 && profile.military_status === 'none') || step === 8) && (
          <div>
            {(
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ color: 'white', marginBottom: '8px' }}>What takes up most of your days right now?</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '14px' }}>Pick one. (Optional)</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '30px' }}>
                  {situationOptions.map(opt => (
                    <span key={opt.value} onClick={() => setProfile({...profile, current_situation: profile.current_situation === opt.value ? '' : opt.value})} style={chipStyle(profile.current_situation === opt.value)}>{opt.label}</span>
                  ))}
                </div>
                <h2 style={{ color: 'white', marginBottom: '8px' }}>What do you do for work?</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '14px' }}>{pilotSetup ? "Your job, your role in service, or anything you'd like to share. (Optional)" : "Your job, your studies, or anything you'd like to share. (Optional)"}</p>
                <input type="text" value={profile.occupation || ''} maxLength={80} aria-label="What do you do for work?"
                  onChange={(e) => setProfile({ ...profile, occupation: e.target.value })}
                  placeholder={pilotSetup ? 'For example: diesel mechanic, Army medic, student' : 'For example: nurse, teacher, student'}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', marginBottom: '30px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', color: 'white', fontSize: '16px', fontFamily: 'inherit' }} />
                <h2 style={{ color: 'white', marginBottom: '8px' }}>What are you working toward?</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '14px' }}>Pick up to two. (Optional)</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {towardOptions.map(opt => (
                    <span key={opt.value} onClick={() => toggleToward(opt.value)} style={chipStyle((profile.working_toward || []).includes(opt.value))}>{opt.label}</span>
                  ))}
                </div>
              </div>
            )}
            <h2 style={{ color: 'white', marginBottom: '8px' }}>Anything else on your heart?</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              Select any additional areas you'd like SAFE to be aware of. (Optional)
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {challengeOptions.map(ch => (
                <span key={ch} onClick={() => toggleChallenge(ch)} style={chipStyle(profile.primary_challenges.includes(ch))}>{ch}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP 8 (no military) or STEP 9 (military): Faith ── */}
        {((step === 8 && profile.military_status === 'none') || step === 9) && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '8px' }}>Faith and Guidance Style</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '24px' }}>
              This personalizes how SAFE speaks with you.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div onClick={() => setProfile({...profile, faith_preference: 'christian', scripture_optin: false})}
                style={cardStyle(profile.faith_preference === 'christian')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>✝️</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>Christian</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Include biblical wisdom and scripture in guidance</div>
                  </div>
                  {profile.faith_preference === 'christian' && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                </div>
              </div>
              <div onClick={() => setProfile({...profile, faith_preference: 'non_christian'})}
                style={cardStyle(profile.faith_preference === 'non_christian')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '28px' }}>🌱</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '16px' }}>Secular Guidance</div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>{pilotSetup ? 'The full framework in its own terms, which speak of Source. No Scripture unless you ask for it.' : 'Practical wisdom with no Scripture, prayer or religious instruction'}</div>
                  </div>
                  {profile.faith_preference === 'non_christian' && <span style={{ marginLeft: 'auto', color: '#4A90E2' }}>✓</span>}
                </div>
              </div>
              {profile.faith_preference === 'non_christian' && (
                <div style={{ padding: '16px 18px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)' }}>
                  <div style={{ color: 'white', fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>Would you like related Scripture offered at the end of replies?</div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '12px' }}>Optional. You can change this any time in Edit Profile.</div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {[[false, 'No, thanks'], [true, 'Yes']].map(([val, label]) => (
                      <button key={label} type="button" onClick={() => setProfile({...profile, scripture_optin: val})}
                        style={{ padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'inherit', color: 'white',
                          border: profile.scripture_optin === val ? '1px solid #4A90E2' : '1px solid rgba(255,255,255,0.2)',
                          background: profile.scripture_optin === val ? 'rgba(74,144,226,0.3)' : 'transparent' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Navigation Buttons ── */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '40px' }}>
          {step > 1 && (
            <button onClick={() => setStep(step - 1)}
              style={{ flex: 1, padding: '16px', background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '12px', color: 'white', fontSize: '16px', cursor: 'pointer' }}>
              Back
            </button>
          )}
          {!isLastStep ? (
            <button onClick={() => setStep(step + 1)} disabled={!canContinue()}
              style={{ flex: 1, padding: '16px', background: canContinue() ? 'linear-gradient(135deg, #4A90E2, #357ABD)' : 'rgba(74,144,226,0.3)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '16px', cursor: canContinue() ? 'pointer' : 'not-allowed', opacity: canContinue() ? 1 : 0.5 }}>
              Continue
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={isLoading || !canSubmit}
              style={{ flex: 1, padding: '16px', background: canSubmit ? 'linear-gradient(135deg, #4A90E2, #357ABD)' : 'rgba(74,144,226,0.3)', border: 'none', borderRadius: '12px', color: 'white', fontSize: '16px', cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5 }}>
              {isLoading ? 'Saving...' : (isEditMode ? 'Update Profile' : 'Start with SAFE')}
            </button>
          )}
        </div>

        {isEditMode && (
          <button onClick={() => navigate('/safe')}
            style={{ width: '100%', marginTop: '12px', padding: '12px', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
            Cancel
          </button>
        )}

      </div>
    </div>
  );
};

export default SAFEOnboarding;
